'use client';

import React, { useEffect, useCallback, useRef, useState } from 'react';
import DashboardTopbar from './DashboardTopbar';
import KpiGrid from './KpiGrid';
import PriceChart from './PriceChart';
import DashboardSkeleton from './DashboardSkeleton';
import ErrorState from './ErrorState';
import PriceLegend from './PriceLegend';
import LastUpdated from './LastUpdated';

export interface HourlyPrice {
  hour: number;      // 0–23
  label: string;     // "00:00", "01:00", etc.
  price: number | null;
  isCurrent: boolean;
}

export interface DashboardData {
  prices: HourlyPrice[];
  currentPrice: number | null;
  average: number;
  minimum: number;
  maximum: number;
  minHour: number;
  maxHour: number;
  currentHour: number;
  fetchedAt: string; // ISO string
}

const AUTO_REFRESH_MS = 60_000;

function parseApiResponse(
  unix_seconds: number[],
  price: (number | null)[],
): DashboardData {
  // Determine current hour in local time
  const now = new Date();
  const currentHour = now.getHours();

  const prices: HourlyPrice[] = unix_seconds.map((unix, i) => {
    const date = new Date(unix * 1000);
    const hour = date.getHours();
    const label = `${String(hour).padStart(2, '0')}:00`;
    return {
      hour,
      label,
      price: price[i] ?? null,
      isCurrent: hour === currentHour,
    };
  });

  const validPrices = prices
    .map((p) => p.price)
    .filter((p): p is number => p !== null);

  const currentPrice =
    prices.find((p) => p.isCurrent)?.price ?? null;

  const average =
    validPrices.length > 0
      ? validPrices.reduce((a, b) => a + b, 0) / validPrices.length
      : 0;

  const minimum = validPrices.length > 0 ? Math.min(...validPrices) : 0;
  const maximum = validPrices.length > 0 ? Math.max(...validPrices) : 0;

  const minHour = prices.find((p) => p.price === minimum)?.hour ?? 0;
  const maxHour = prices.find((p) => p.price === maximum)?.hour ?? 0;

  return {
    prices,
    currentPrice,
    average,
    minimum,
    maximum,
    minHour,
    maxHour,
    currentHour,
    fetchedAt: now.toISOString(),
  };
}

export default function DashboardShell() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    else if (!data) setLoading(true);

    setError(null);

    try {
      // BACKEND INTEGRATION POINT:
      // This fetches from our Next.js API route which proxies the Energy-Charts API
      const res = await fetch('/api/boersenstrompreis', {
        cache: 'no-store',
      });

      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(
          json?.error || `HTTP ${res.status}: Daten konnten nicht geladen werden.`
        );
      }

      const json = await res.json();

      if (!json.unix_seconds || !json.price) {
        throw new Error('Ungültiges Datenformat von der API erhalten.');
      }

      const parsed = parseApiResponse(json.unix_seconds, json.price);
      setData(parsed);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Unbekannter Fehler beim Datenabruf.';
      setError(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [data]);

  // Initial load
  useEffect(() => {
    fetchData(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      fetchData(false);
    }, AUTO_REFRESH_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleManualRefresh = () => {
    fetchData(true);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <DashboardTopbar
        onRefresh={handleManualRefresh}
        refreshing={refreshing}
        lastUpdated={data?.fetchedAt ?? null}
      />

      <main className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 py-8">
        {/* Section Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-semibold text-foreground tracking-tight">
              Börsenstrompreis Heute
            </h1>
            {!loading && !error && data && (
              <span className="flex items-center gap-1.5">
                <span className="pulse-dot" />
                <span className="label-caps text-primary">Live</span>
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Day-Ahead · DE-LU · Fraunhofer ISE Energy-Charts
          </p>
        </div>

        {/* Loading State */}
        {loading && <DashboardSkeleton />}

        {/* Error State */}
        {!loading && error && (
          <ErrorState message={error} onRetry={handleManualRefresh} />
        )}

        {/* Data State */}
        {!loading && !error && data && (
          <div className="fade-in space-y-6">
            <KpiGrid data={data} />

            <div className="card-surface p-5 sm:p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="label-caps mb-1">
                    Preisverlauf · 24 Stunden
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(data.fetchedAt).toLocaleDateString('de-DE', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
                <PriceLegend />
              </div>
              <PriceChart prices={data.prices} />
            </div>

            <LastUpdated fetchedAt={data.fetchedAt} />
          </div>
        )}
      </main>
    </div>
  );
}